// PRODUCT-26110 Phase 2B2: vue-router 3's default export was the Router class, used
// here purely as a type (`public router!: _Router`). vue-router 4 has no default
// export at all (named exports only) and no class to instantiate — `Router` is the
// instance-shape interface. See types/Application.ts for the matching options-type fix.
import { createApp, h, type App, type ComponentPublicInstance } from 'vue';

import type { Router as _Router } from 'vue-router';

import type { Pinia } from 'pinia';

import ApplicationObserver from '@evidentid/vue-commons/core/ApplicationObserver';

import { createModalPlugin } from '../../components/Modal';
import { createAppPinia } from '../pinia';
import { createProcedurePlugin } from '../../components/ProcedureManager';
import {
    AppEvents,
    Application as ApplicationInterface,
    BaseApplicationOptions,
    BaseApplicationStore,
} from '../types/Application';

export class Application<
    S extends BaseApplicationStore,
    O extends {},
    V extends {},
    P extends { [key: string]: object },
> implements ApplicationInterface<S, O, V, P> {
    private listeners: Record<string, ((...args: any) => any)[]> = {};
    private root: Element | null = null;
    private vue: ComponentPublicInstance | null = null;
    // PRODUCT-26110 Phase 2B2: holds the real Vue 3 `createApp()` instance — needed
    // separately from `this.vue` (the mounted component instance) since unmounting
    // under Vue 3 is `app.unmount()`, not a method on the component instance itself
    // (Vue 2's `.$destroy()`, which no longer exists). See mount()/destroyMountedComponent().
    private vueApp: ReturnType<typeof createApp> | null = null;
    // Pinia instance for store modules migrated off Vuex; carries the `$app`
    // plugin so store actions can reach API clients (see ../pinia.ts).
    private pinia: Pinia = createAppPinia(this);
    // PRODUCT-26110 Phase 2B2: vue-router 4's beforeEach/afterEach/beforeResolve return
    // an unregister function instead of exposing mutable `afterHooks`/`beforeHooks`/
    // `resolveHooks` arrays (vue-router-3-only internals) — captured here so destroy()
    // can undo the `router.afterEach(...)` subscription below.
    private unregisterRouteChangeListener: (() => void) | null = null;
    private readonly _buildVueContext: (app: Application<S, O, V, P>) => V;
    private readonly mainComponent: any;
    // PRODUCT-26110 Task 14: built during initialize() (module injections /
    // buildProceduresMap() / buildSlotComponents() are only available once it has
    // run), but can no longer be *applied* via the legacy `Vue.use()`/`Vue.mixin()`
    // global singleton (removed with @vue/compat) -- application now happens in
    // mount(), against the real createApp() instance, exactly like `appPlugins`/
    // store/router already do. See mount() below.
    private modalPlugin!: { install(app: App): void };
    private procedurePlugin!: { install(app: App): void };
    private vueContext!: Record<string, any>;

    public readonly options: Required<BaseApplicationOptions> & O;
    public router!: _Router;
    public observer!: ApplicationObserver<any, any, any>;

    public constructor(
        options: Required<BaseApplicationOptions> & O,
        buildVueContext: (app: Application<S, O, V, P>) => V,
        mainComponent: any,
    ) {
        this.options = options;
        this._buildVueContext = buildVueContext;
        this.mainComponent = mainComponent;
    }

    protected async initialize({
        initialize,
        createRouter,
        buildSlotComponents,
        buildProceduresMap,
    }: {
        initialize: (app: Application<S, O, V, P>) => Promise<any>;
        createRouter: (app: Application<S, O, V, P>) => _Router;
        buildSlotComponents: () => Record<string, any[]>;
        buildProceduresMap: () => Record<string, any>;
    }) {
        // Must happen before module injection below and before ApplicationBuilder's
        // `execute()` hooks, both of which create Pinia stores: Pinia holds plugins
        // registered before installation in a pending queue and only moves them into
        // its active list when `app.use(pinia)` runs, then applies them solely at
        // store-creation time. A store created before that point never receives
        // `$app` (see ../pinia.ts) and every action reaching an API client through it
        // fails with "Cannot read properties of undefined".
        this.ensureVueApp();
        // PRODUCT-26110 Phase 2B2: vue-router 4 / vuex 4 install via `app.use()` only,
        // once a real `createApp()` instance exists (see mount() below).
        //
        // PRODUCT-26110 Task 14: Modal/ProcedureManager (Tooltip and PortalVue are
        // already gone -- see below) no longer install via the legacy `Vue.use()`
        // global singleton (removed with @vue/compat) -- their plugin objects are
        // built here (buildProceduresMap() is only available in this scope) and
        // applied in mount() against the real createApp() instance instead, the same
        // place appPlugins/store/router already install.
        //
        // Tooltip is no longer registered here: floating-vue (its v-tooltip
        // replacement, PRODUCT-26110 Phase 2B2 Task 1) is Vue-3-only and never had a
        // `@vue/compat` global-singleton install path. It uses the same `appPlugins`
        // hook Quasar 2 uses -- see tprm-portal's main.ts / quasar.ts, and
        // Tooltip/createTooltipPlugin.ts's `installFloatingVue` export.
        //
        // PRODUCT-26110 Task 8b Step 2: PortalVue is no longer registered here either
        // -- Modal.vue/ModalTarget.vue/Popover.vue were migrated to native <Teleport>,
        // which needs no plugin install.
        this.modalPlugin = createModalPlugin();
        this.procedurePlugin = createProcedurePlugin(buildProceduresMap());

        // Inject modules
        await initialize(this);

        // Set-up router — must exist before mount() calls app.use(), since
        // vue-router 4 can only be installed onto a real createApp() app instance
        // (see mount() below).
        this.router = createRouter(this);

        // Set up application observer
        // Store-less: only the router-based observer hooks (onRouteChange/onRoute)
        // remain usable; the Vuex store methods are gone with the Pinia migration.
        this.observer = new ApplicationObserver({ router: this.router });

        // Pass down information about routing.
        // PRODUCT-26110 Phase 2B2: vue-router 4 fires afterEach on aborted/cancelled/
        // duplicated navigations too (v3 fired only on successful ones); skip those so
        // `routeChange` consumers (page title, tracking) don't treat a failed navigation
        // as a page view.
        this.unregisterRouteChangeListener = this.router.afterEach(
            (to, from, failure) => (failure ? undefined : this.emit('routeChange', to, from)),
        );

        // PRODUCT-26110 Task 14: the vueContext mixin is built here (buildVueContext()/
        // buildSlotComponents() are only available in this scope) but, like the Modal/
        // ProcedureManager plugins above, can no longer be applied via the legacy
        // `Vue.mixin()` global singleton -- stored for mount() to apply via the real
        // app instance's `app.mixin()` instead.
        this.vueContext = {
            ...this.buildVueContext(),
            $slotComponents: buildSlotComponents(),
        };
    }

    public executeProcedure<K extends keyof P>(key: K, options: P[K], done: (modified: boolean) => any): void {
        // TODO: gain access to ProcedureManager from Application
        throw new Error('Not implemented yet');
    }

    public buildVueContext(): V {
        return this._buildVueContext(this);
    }

    public mount(element: Element): void {
        if (this.root) {
            this.destroyMountedComponent();
        }

        this.emit('beforeMount', element);
        this.root = element;
        // PRODUCT-26110 Phase 2B2: `router`/`store` as *root options* to `new Vue(...)`
        // is a vue-router-2/3 + vuex-2/3 pattern (injected via mixins their
        // `Vue.use()` installs onto every component). vue-router 4 / vuex 4 have no
        // such mixin — installation instead does `app.provide(routerKey, router)` /
        // `app.provide(storeKey, store)` inside their own `install(app)`, which
        // requires a real Vue 3 `app` object to exist first. So construction order is:
        // create the real app via createApp(), THEN app.use(store)/app.use(router),
        // THEN mount — matching the Phase 2A spike's boot probe (router navigated,
        // URL normalized to #/, store reactive).
        //
        // The app instance and its Pinia installation come from initialize() (see
        // ensureVueApp); the router cannot, since it is only built at the end of
        // initialize() and re-mounting must pick up the current one.
        const app = this.ensureVueApp();
        app.use(this.router);

        // PRODUCT-26110 Task 14: Modal/ProcedureManager plugins + the vueContext mixin
        // built in initialize() (see there), applied here against the real Vue 3 app
        // instance now that @vue/compat's global `Vue.use()`/`Vue.mixin()` singleton
        // is gone.
        app.use(this.modalPlugin);
        app.use(this.procedurePlugin);
        const vueContext = this.vueContext;
        app.mixin({
            beforeCreate() {
                Object.assign(this, vueContext);
            },
        });

        // PRODUCT-26110 Task 14: the legacy `Vue.config.errorHandler` global-singleton
        // assignment (previously set in initialize(), before the app instance existed)
        // moves here onto the real per-instance app -- Vue 3 error handling is
        // app-scoped, not global. A freshly created app always starts with
        // `app.config.errorHandler === undefined`, so there's no prior handler left to
        // preserve/chain (the old chaining logic is now moot, not just dead).
        app.config.errorHandler = (error) => {
            // Vue 3's real `ErrorHandler` type passes `error: unknown` (an error
            // boundary can technically receive anything thrown) -- `AppEvents.
            // vueError` keeps its existing `Error` contract for consumers (e.g.
            // persist-vue-errors), matching what every Vue error in practice actually
            // is and what this call site already assumed before `config` gained a
            // real type.
            this.emit('vueError', error as Error);
        };

        // PRODUCT-26110 Phase 2B2 Task 1 (Quasar 2): app-level plugins that need a
        // real Vue 3 `App` (e.g. Quasar 2's `app.use(Quasar, quasarUserOptions)`,
        // which internally calls `app.directive()`/`app.config.globalProperties`
        // -- neither of which the `@vue/compat` global singleton's `Vue.use()`
        // shim could satisfy). See types/Application.ts's `appPlugins` for the hook
        // and quasar.ts's `installQuasar` for the tprm-portal caller.
        this.options.appPlugins.forEach((installPlugin) => installPlugin(app));
        this.vue = app.mount(element);
        this.emit('afterMount', element);
    }

    public on<K extends keyof AppEvents>(name: K, fn: (...args: Parameters<AppEvents[K]>) => any): () => void {
        const nameStr = name as string;
        const wrappedFn: typeof fn = (...args) => fn(...args);
        if (!this.listeners[nameStr]) {
            this.listeners[nameStr] = [];
        }
        this.listeners[nameStr].push(wrappedFn);
        return () => {
            const index = (this.listeners[nameStr] || []).indexOf(wrappedFn);
            if (index !== -1) {
                this.listeners[nameStr].splice(index, 1);
            }
        };
    }

    public once<K extends keyof AppEvents>(name: K, fn: (...args: Parameters<AppEvents[K]>) => any): () => void {
        const unsubscribe = this.on(name, (...args) => {
            unsubscribe();
            fn(...args);
        });
        return unsubscribe;
    }

    public emit<K extends keyof AppEvents>(name: K, ...args: Parameters<AppEvents[K]>): void {
        const listeners = this.listeners[name as string];
        if (listeners) {
            // Use copy of listeners and ensure that all listeners are fired,
            // otherwise, when some listeners are unsubscribed during emit,
            // some may be omitted.
            for (const listener of listeners.slice()) {
                listener(...args as any);
            }
        }
    }

    public getPinia(): Pinia {
        return this.pinia;
    }

    public destroy() {
        this.emit('beforeDestroy');

        // Destroy Vue component
        this.destroyMountedComponent();

        // Unsubscribe from router: vue-router 4 has no `afterHooks`/`beforeHooks`/
        // `resolveHooks` arrays and no public `.history.listeners`/`.errorCbs`/
        // `.readyCbs`/`.readyErrorCbs`/`.cb` to null out (those were vue-router-3-only
        // internals). v4's beforeEach/afterEach/beforeResolve each return an
        // unregister function instead — captured in initialize() and called here.
        if (this.unregisterRouteChangeListener) {
            this.unregisterRouteChangeListener();
            this.unregisterRouteChangeListener = null;
        }

        this.emit('afterDestroy');

        // Destroy own listeners
        this.listeners = {};
    }

    // Creates the Vue app instance and installs Pinia on it. Called from initialize()
    // so stores built during module injection get `$app`, and again from mount() after
    // a destroy() has cleared the previous instance (a Vue 3 app cannot be re-mounted
    // once unmounted). Installing the same Pinia into a replacement app is safe: its
    // plugin list is already active, so nothing is registered twice.
    private ensureVueApp(): ReturnType<typeof createApp> {
        if (!this.vueApp) {
            this.vueApp = createApp({ render: () => h(this.mainComponent) });
            this.vueApp.use(this.pinia);
        }
        return this.vueApp;
    }

    private destroyMountedComponent(): void {
        // Get back previous element
        const element = this.vue?.$el;
        if (element?.parentNode) {
            element.parentNode.replaceChild(this.root!, element);
        }

        // Destroy Vue app instance: createApp().unmount() replaces Vue 2's
        // `new Vue(...).$destroy()` under Vue 3 — the mounted component instance
        // itself (this.vue) has no $destroy() method any more. The instance now
        // exists from initialize() onwards, so only unmount one that was mounted.
        if (this.vue) {
            this.vueApp?.unmount();
        }

        // Destroy observer listeners
        this.observer.removeListeners();

        // Remove references
        this.vue = null;
        this.vueApp = null;
        this.root = null;
    }
}
